import { Button, Stack } from '@mui/material';
import SvgIconStyle from './SvgIconStyle';
import useLocales from '@hooks/useLocales';
import { useRouter } from 'next/router';
import { FC } from 'react';
import Image from '@components/Image';
import { Item, styles } from './styles';
import BetaChip from './BetaChip';

type Props = {
  connectionName?: string;
  isError: boolean;
  isLicenseNational?: boolean;
  name?: string;
  title?: string;
};

const Heading: FC<Props> = ({ connectionName, isError, isLicenseNational, name, title }) => {
  const { t } = useLocales();

  const router = useRouter();
  const { pathname } = router;

  const text = (param: string): { text: string; button: boolean } => {
    switch (param) {
      case '/loan-officers/dashboard':
        return { text: t('header.dashboard'), button: false };
      case '/user-profile':
        return { text: t('header.user_profile'), button: true };
      case '/companies/[companyLicense]':
        return { text: title || 'Company Profile', button: true };
      case '/companies/[companyLicense]/agent-transactions/[agentId]':
        return { text: t('header.agent_transactions'), button: true };
      case '/loan-officers/lists':
        return { text: t('header.lists.loan_officers'), button: false };
      case '/loan-officers/lists/[listId]':
        return { text: t('header.lists.loan_officers'), button: true };
      case '/loan-officers/trends':
        return { text: t('header.employee_trends'), button: false };
      case '/loan-officers/[nmls]':
        return { text: t('header.loan_officer_profile'), button: true };
      case '/loan-officers/[nmls]/companies/[license]':
        return { text: t('header.loan_officers_company'), button: true };
      case '/loan-officers/[nmls]/agent-transactions/[agentId]':
        return { text: t('header.agent_transactions'), button: true };
      case '/loan-officers/search':
        return { text: t('header.loan_officers_search'), button: false };
      case '/loan-officers/search/results':
        return { text: t('header.loan_officers_search'), button: true };
      case '/loan-officers/company-trends/search':
        return { text: t('header.company_trends_search'), button: false };
      case '/loan-officers/company-trends/search/results':
        return { text: t('header.company_trends_search'), button: true };
      case '/loan-officers/company-trends/[license]':
        return { text: t('header.employee_trends'), button: true };
      case '/agents/[id]':
        return { text: t('header.agent_profile'), button: true };
      case '/agents/search':
        return { text: t('header.agents_search'), button: false };
      case '/agents/search/results':
        return { text: t('header.agents_search'), button: true };
      case '/agents/teams/search':
        return { text: t('header.agent_teams_search'), button: false };
      case '/agents/teams/search/results':
        return { text: t('header.agent_teams_search'), button: true };
      case '/agents/lists':
        return { text: t('header.lists.agents'), button: false };
      case '/agents/lists/[listId]':
        return { text: t('header.lists.agents'), button: true };
      case '/opportunities/search':
        return { text: t('header.opportunities_search'), button: false };
      case '/opportunities/search/results':
        return { text: t('header.opportunities_search'), button: true };
      case '/opportunities/lists':
        return { text: t('header.lists.opportunities'), button: false };
      case '/opportunities/lists/[listId]':
        return { text: t('header.lists.opportunities'), button: true };
      default:
        return { text: '', button: false };
    }
  };

  return (
    <Stack direction="row">
      {text(pathname).button && (
        <Button
          startIcon={
            <SvgIconStyle
              src="/icons/iconly/light/arrow_left_square.svg"
              sx={styles.backButton.icon}
            />
          }
          size="small"
          sx={styles.backButton.sx}
          onClick={() => router.back()}
          data-role="back-button"
        >
          {t('header.back_button')}
        </Button>
      )}
      <Stack direction="row" alignItems="center" spacing="10px" ml="32px">
        {isError ? (
          <Item sx={styles.fonts.text}>{t('header.404')}</Item>
        ) : pathname == '/loan-officers/[nmls]' ? (
          <>
            <Item sx={styles.fonts.text}>{name && name.toLowerCase()}</Item>
            {isLicenseNational && <Image alt={'some'} src="/icons/iconly/light/n_circle.svg" />}
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : pathname == '/companies/[companyLicense]/agent-transactions' ? (
          <>
            <Item sx={styles.fonts.main}>{name && name.toLowerCase()}</Item>
            <Item sx={styles.fonts.text}>/</Item>
            <Item sx={styles.fonts.text}>{connectionName && connectionName.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : pathname == '/loan-officers/[nmls]/agent-transactions' ? (
          <>
            <Item sx={styles.fonts.main}>{name && name.toLowerCase()}</Item>
            {isLicenseNational && <Image alt={'some'} src="/icons/iconly/light/n_circle.svg" />}
            <Item sx={styles.fonts.text}>/</Item>
            <Item sx={styles.fonts.text}>{connectionName && connectionName.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : pathname == '/agents/[id]' ? (
          <>
            <Item sx={styles.fonts.text}>{name && name.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : pathname == '/loan-officers/[nmls]/companies/[license]' ? (
          <>
            <Item sx={styles.fonts.text}>{name && name.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : pathname == '/companies/[companyLicense]' ? (
          <>
            <Item sx={styles.fonts.text}>{name && name.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
            <BetaChip boxProps={{ sx: { pl: '10px' } }} />
          </>
        ) : pathname == '/loan-officers/company-trends/[license]' ? (
          <>
            <Item sx={styles.fonts.text}>{name && name.toLowerCase()}</Item>
            <Item>—</Item>
            <Item>{text(pathname).text}</Item>
          </>
        ) : (
          <Item sx={styles.fonts.text}>{text(pathname).text}</Item>
        )}
      </Stack>
    </Stack>
  );
};

export default Heading;
